/**
 * Two-factor authentication (TOTP) enrolment — security settings card on
 * the Profile page.
 *
 * Flow: status → (if not enrolled) "Set up" → scan QR / copy key →
 * confirm a live 6-digit code → enrolled. The shared secret never leaves
 * the server in plaintext; the FE only sees the provisioning URI / QR
 * during enrolment, and the code the user types is validated server-side.
 *
 * Once enrolled, high-risk actions (changing a withdrawal destination
 * account, requesting a withdrawal) require a fresh single-use code from
 * the same authenticator app.
 */
import { useCallback, useEffect, useState } from 'react';
import authService, { type MfaSetup } from '../../services/auth.service';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import RecoveryCodesPanel from './RecoveryCodesPanel';
import RecoveryCodesManager from './RecoveryCodesManager';
import PasskeysManager from './PasskeysManager';
import { colors } from '../../styles/theme';

type Mode = 'idle' | 'enrolling' | 'reveal';

interface Props {
  /** Told once the status is known and whenever it changes, so a card that needs the factor
   *  (a step-up asked of a client who has none) can show its own field the moment it is on. */
  readonly onEnrolledChange?: (enrolled: boolean) => void;
}

export default function MfaEnrollmentSection({ onEnrolledChange }: Props = {}): JSX.Element {
  const [enrolled, setEnrolled] = useState<boolean | null>(null);
  const [recoveryRemaining, setRecoveryRemaining] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>('idle');
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [code, setCode] = useState('');
  const [newCodes, setNewCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const s = await authService.getMfaStatus();
      setEnrolled(s.enrolled);
      setRecoveryRemaining(s.recovery_codes_remaining);
    } catch {
      setEnrolled(null);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (enrolled !== null) onEnrolledChange?.(enrolled);
  }, [enrolled, onEnrolledChange]);

  const beginSetup = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await authService.setupMfa();
      setSetup(data);
      setMode('enrolling');
    } catch (e: unknown) {
      // Already enrolled (409) — just refresh the status badge.
      if (getApiErrorCode(e) === 'mfa_already_enrolled') {
        await loadStatus();
        setMode('idle');
      } else {
        setError(getApiErrorMessage(e, 'Could not start two-factor setup.'));
      }
    } finally {
      setBusy(false);
    }
  }, [loadStatus]);

  const confirm = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const s = await authService.confirmMfa(code.trim());
      setEnrolled(s.enrolled);
      setRecoveryRemaining(s.recovery_codes_remaining);
      // Reveal the freshly-minted recovery codes ONCE before returning to
      // the status view.
      setNewCodes(s.recovery_codes);
      setMode('reveal');
      setSetup(null);
      setCode('');
      setFlash('Two-factor authentication is now on.');
      globalThis.setTimeout(() => setFlash(null), 6000);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'That code did not match — try the current one.'));
    } finally {
      setBusy(false);
    }
  }, [code]);

  const cancel = useCallback(() => {
    setMode('idle');
    setSetup(null);
    setCode('');
    setError(null);
  }, []);

  return (
    <section className="mt-6">
      <h2 className="text-sm font-bold uppercase tracking-widest mb-3"
          style={{ color: colors.inkSoft }}>
        Two-factor authentication
      </h2>

      <div className="rounded-2xl p-5 max-w-2xl"
           style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>

        {flash && (
          <div className="rounded-lg p-3 mb-3 text-sm"
               style={{ background: colors.successBg, color: colors.success,
                        border: `1px solid ${colors.successBorder}` }}>
            {flash}
          </div>
        )}

        {error && (
          <div className="rounded-lg p-3 mb-3 text-sm" role="alert"
               style={{ background: colors.dangerBg, color: colors.danger,
                        border: `1px solid ${colors.dangerBorder}` }}>
            {error}
          </div>
        )}

        {/* Status line */}
        {enrolled !== null && mode === 'idle' && (
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium" style={{ color: colors.ink }}>
                {enrolled ? 'Protected with an authenticator app' : 'Not set up yet'}
              </div>
              <p className="text-xs mt-1" style={{ color: colors.muted }}>
                {enrolled
                  ? 'You will be asked for a code when you change a withdrawal '
                    + 'account or request a withdrawal.'
                  : 'Add a one-time code from an authenticator app (Google '
                    + 'Authenticator, Authy, 1Password) to approve withdrawals '
                    + 'and bank-account changes.'}
              </p>
            </div>
            <span className="shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full"
                  style={{
                    background: enrolled ? colors.successBg : colors.canvas,
                    color: enrolled ? colors.success : colors.muted,
                    border: `1px solid ${enrolled ? colors.successBorder : colors.border}`,
                  }}>
              {enrolled ? 'On' : 'Off'}
            </span>
          </div>
        )}

        {/* Enrolled extras: recovery codes + passkeys */}
        {enrolled && mode === 'idle' && (
          <>
            <RecoveryCodesManager remaining={recoveryRemaining} onRegenerated={loadStatus} />
            <PasskeysManager />
          </>
        )}

        {/* One-time recovery-code reveal right after enrolment */}
        {mode === 'reveal' && (
          <div>
            <RecoveryCodesPanel codes={newCodes} />
            <button
              type="button"
              onClick={() => { setMode('idle'); setNewCodes([]); }}
              className="mt-3 text-sm px-3 py-1.5 rounded-lg font-semibold"
              style={{ background: colors.gold, color: colors.navy }}
            >
              I&apos;ve saved these
            </button>
          </div>
        )}

        {!enrolled && mode === 'idle' && (
          <button
            type="button"
            onClick={() => { beginSetup(); }}
            disabled={busy}
            className="mt-4 text-sm px-3 py-1.5 rounded-lg font-semibold"
            style={{ background: colors.gold, color: colors.navy }}
          >
            {busy ? 'Starting…' : 'Set up two-factor authentication'}
          </button>
        )}

        {/* Enrolment wizard */}
        {mode === 'enrolling' && setup && (
          <div>
            <ol className="text-sm space-y-3" style={{ color: colors.ink }}>
              <li>
                <span className="font-medium">1.</span> Scan this QR code with your
                authenticator app, or enter the key manually.
                <div className="mt-3 flex flex-col sm:flex-row items-start gap-4">
                  <img
                    src={setup.qr_data_uri}
                    alt="Two-factor authentication QR code"
                    width={176}
                    height={176}
                    className="rounded-lg"
                    style={{ border: `1px solid ${colors.border}`, background: '#fff' }}
                  />
                  <div className="min-w-0">
                    <div className="text-xs mb-1" style={{ color: colors.muted }}>
                      Manual setup key
                    </div>
                    <code className="block text-xs break-all p-2 rounded-md font-mono"
                          style={{ background: colors.canvas, color: colors.inkSoft,
                                   border: `1px solid ${colors.border}` }}>
                      {extractSecret(setup.provisioning_uri)}
                    </code>
                  </div>
                </div>
              </li>
              <li>
                <span className="font-medium">2.</span> Enter the 6-digit code the
                app shows now.
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    placeholder="123456"
                    className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest w-36"
                    style={{ background: colors.surface, color: colors.ink,
                             border: `1px solid ${colors.border}` }}
                  />
                </div>
              </li>
            </ol>

            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={cancel}
                      className="text-sm px-3 py-1.5 rounded-lg"
                      style={{ background: 'transparent', color: colors.inkSoft,
                               border: `1px solid ${colors.border}` }}>
                Cancel
              </button>
              <button type="button" onClick={() => { confirm(); }}
                      disabled={busy || code.trim().length < 6}
                      className="text-sm px-3 py-1.5 rounded-lg font-semibold"
                      style={{ background: colors.gold, color: colors.navy }}>
                {busy ? 'Verifying…' : 'Verify & turn on'}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/** Pull the base32 secret out of an `otpauth://` URI for manual entry. */
function extractSecret(uri: string): string {
  try {
    const params = new URL(uri).searchParams;
    return params.get('secret') ?? uri;
  } catch {
    return uri;
  }
}
